import { Search, Plus, Edit2, Trash2 } from "lucide-react";
import { useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import dayjs from "dayjs";
import { toast } from "sonner";

export const TemplateList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: templates,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["templates"],
    queryFn: async () => {
      const { data: templatesData, error: templatesError } = await supabase
        .from("templates")
        .select(
          `
          *,
          template_search_terms (
            term
          )
        `,
        )
        .order("created_at", { ascending: false });

      if (templatesError) throw templatesError;
      return templatesData;
    },
  });

  const createTemplateMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .from("templates")
        .insert([{ name: "Untitled Template" }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (newTemplate) => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast.success("Template created successfully");
      navigate(`/admin/template-editor/${newTemplate.id}`);
    },
    onError: (error) => {
      console.error("Error creating template:", error);
      toast.error("Failed to create template");
    },
  });

  const handleCreateTemplate = async () => {
    createTemplateMutation.mutate();
  };

  const handleDelete = async (id: string) => {
    const promise = new Promise<void>((resolve, reject) => {
      const confirmed = window.confirm("Are you sure you want to delete this template? This action cannot be undone.");
      if (confirmed) {
        Promise.resolve(supabase.from("templates").delete().eq("id", id))
          .then(({ error }) => {
            if (error) reject(error);
            else resolve();
          })
          .catch(reject);
      } else {
        reject(new Error("Delete cancelled"));
      }
    });

    toast.promise(promise, {
      loading: "Deleting template...",
      success: () => {
        refetch();
        return "Template deleted successfully";
      },
      error: (err) => {
        console.error("Error deleting template:", err);
        return err.message === "Delete cancelled" ? "Template deletion cancelled" : "Failed to delete template";
      },
    });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="relative w-64">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search templates..." className="pl-8" />
        </div>
        <Button className="bg-primary hover:bg-primary/90" onClick={handleCreateTemplate}>
          <Plus className="mr-2 h-4 w-4" /> New Template
        </Button>
      </div>

      <div className="grid gap-4">
        {templates?.map((template) => (
          <Card key={template.id} className="p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="flex-grow">
                <h3 className="font-medium text-lg">{template.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {template.template_search_terms?.map((term) => term.term).join(", ")}
                </p>
                <div className="text-sm text-muted-foreground">
                  Last modified: {dayjs(template.updated_at).tz().format("DD/MM/YYYY")}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate(`/admin/template-editor/${template.id}`)}>
                  <Edit2 className="h-4 w-4" />
                  <span className="sr-only">Edit</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => handleDelete(template.id)}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete</span>
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
